import * as THREE from 'three';
import { CAR } from '../../utils/constants';

export type DrivingVfxInput = {
  x: number;
  z: number;
  heading: number;
  speed: number;
  handbrake: boolean;
  nitroBoosting: boolean;
  /** Forward throttle 0–1 — idle exhaust wisps. */
  throttle: number;
  /** >0 when a wall/building hit this frame (sparks). */
  collisionImpulse: number;
  /** True when scraping against a wall. */
  wallScraping?: boolean;
  /** Drift amount 0-1 for enhanced tire marks. */
  driftAmount?: number;
  /** True when revving hard (high throttle, low speed) - for burnout smoke. */
  revving?: boolean;
  /** RPM ratio 0-1 for exhaust flames. */
  rpmRatio?: number;
};

function rearWheel(
  x: number,
  z: number,
  heading: number,
  sign: number,
): THREE.Vector3 {
  const d = CAR.LENGTH * 0.44;
  const rx = Math.cos(heading) * CAR.WIDTH * 0.46 * sign;
  const rz = -Math.sin(heading) * CAR.WIDTH * 0.46 * sign;
  return new THREE.Vector3(
    x - Math.sin(heading) * d + rx,
    CAR.HEIGHT * 0.52 + 0.06,
    z - Math.cos(heading) * d + rz,
  );
}

function exhaustWorld(
  x: number,
  z: number,
  heading: number,
  sign: number,
): THREE.Vector3 {
  const L = CAR.LENGTH * 0.42;
  const W = CAR.WIDTH * 0.38;
  const lx = sign * W;
  const lz = -L;
  const wx = x + Math.cos(heading) * lx + Math.sin(heading) * lz;
  const wz = z - Math.sin(heading) * lx + Math.cos(heading) * lz;
  return new THREE.Vector3(wx, CAR.HEIGHT * 0.52 - CAR.HEIGHT * 0.32, wz);
}

type SmokeP = {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  max: number;
  on: boolean;
};

type NitroP = {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  max: number;
  on: boolean;
};

type SparkP = {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  max: number;
  on: boolean;
};

/**
 * Realistic driving VFX: tire marks, drift smoke, nitro flames, collision sparks.
 * No fake arcade trails - just real-looking effects.
 */
export class DrivingVfx {
  private readonly root = new THREE.Group();
  private readonly smokePts: THREE.Points;
  private readonly smokeGeo: THREE.BufferGeometry;
  private readonly smokeMat: THREE.PointsMaterial;
  private readonly smokePool: SmokeP[];
  private readonly nitroPts: THREE.Points;
  private readonly nitroGeo: THREE.BufferGeometry;
  private readonly nitroMat: THREE.PointsMaterial;
  private readonly nitroPool: NitroP[];
  private readonly sparkPts: THREE.Points;
  private readonly sparkGeo: THREE.BufferGeometry;
  private readonly sparkMat: THREE.PointsMaterial;
  private readonly sparkPool: SparkP[];
  private readonly tireGeo: THREE.BufferGeometry;
  private readonly tireMesh: THREE.Mesh;
  private tireVertexCount = 0;
  private readonly tireMaxFloats: number;
  private lastTireLX = 0;
  private lastTireLZ = 0;
  private lastTireRX = 0;
  private lastTireRZ = 0;
  private hadTireL = false;
  private hadTireR = false;
  private smokeAcc = 0;
  private nitroAcc = 0;
  private exhaustFlameAcc = 0;
  private burnoutSmokeAcc = 0;
  
  /** Exhaust flame particles - fire from exhaust when revving hard */
  private readonly exhaustFlamePool: NitroP[] = [];
  private readonly exhaustFlamePts: THREE.Points;
  private readonly exhaustFlameGeo: THREE.BufferGeometry;
  private readonly exhaustFlameMat: THREE.PointsMaterial;

  constructor(parent: THREE.Scene) {

    // OPTIMIZED particle counts for performance
    const smokeCount = 40;
    this.smokePool = [];
    const sPos = new Float32Array(smokeCount * 3);
    const sCol = new Float32Array(smokeCount * 3);
    const sSizes = new Float32Array(smokeCount);
    for (let i = 0; i < smokeCount; i++) {
      this.smokePool.push({
        x: 0,
        y: 0,
        z: 0,
        vx: 0,
        vy: 0,
        vz: 0,
        life: 0,
        max: 1,
        on: false,
      });
      sSizes[i] = 1.0;
    }
    this.smokeGeo = new THREE.BufferGeometry();
    this.smokeGeo.setAttribute('position', new THREE.BufferAttribute(sPos, 3));
    this.smokeGeo.setAttribute('color', new THREE.BufferAttribute(sCol, 3));
    this.smokeGeo.setAttribute('size', new THREE.BufferAttribute(sSizes, 1));
    this.smokeMat = new THREE.PointsMaterial({
      size: 4.5,
      transparent: true,
      opacity: 0.75,
      depthWrite: false,
      sizeAttenuation: true,
      vertexColors: true,
    });
    this.smokePts = new THREE.Points(this.smokeGeo, this.smokeMat);
    this.root.add(this.smokePts);

    const nitroCount = 24;
    this.nitroPool = [];
    const nPos = new Float32Array(nitroCount * 3);
    const nCol = new Float32Array(nitroCount * 3);
    for (let i = 0; i < nitroCount; i++) {
      this.nitroPool.push({
        x: 0,
        y: 0,
        z: 0,
        vx: 0,
        vy: 0,
        vz: 0,
        life: 0,
        max: 1,
        on: false,
      });
    }
    this.nitroGeo = new THREE.BufferGeometry();
    this.nitroGeo.setAttribute('position', new THREE.BufferAttribute(nPos, 3));
    this.nitroGeo.setAttribute('color', new THREE.BufferAttribute(nCol, 3));
    this.nitroMat = new THREE.PointsMaterial({
      size: 0.8,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
      vertexColors: true,
    });
    this.nitroPts = new THREE.Points(this.nitroGeo, this.nitroMat);
    this.root.add(this.nitroPts);

    // EXHAUST FLAMES - fire from exhaust when revving/throttle high
    const exhaustFlameCount = 16;
    const efPos = new Float32Array(exhaustFlameCount * 3);
    const efCol = new Float32Array(exhaustFlameCount * 3);
    for (let i = 0; i < exhaustFlameCount; i++) {
      this.exhaustFlamePool.push({
        x: 0, y: 0, z: 0,
        vx: 0, vy: 0, vz: 0,
        life: 0, max: 1, on: false,
      });
    }
    this.exhaustFlameGeo = new THREE.BufferGeometry();
    this.exhaustFlameGeo.setAttribute('position', new THREE.BufferAttribute(efPos, 3));
    this.exhaustFlameGeo.setAttribute('color', new THREE.BufferAttribute(efCol, 3));
    this.exhaustFlameMat = new THREE.PointsMaterial({
      size: 1.2,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
      vertexColors: true,
    });
    this.exhaustFlamePts = new THREE.Points(this.exhaustFlameGeo, this.exhaustFlameMat);
    this.root.add(this.exhaustFlamePts);

    const sparkCount = 10;
    this.sparkPool = [];
    const kPos = new Float32Array(sparkCount * 3);
    const kCol = new Float32Array(sparkCount * 3);
    for (let i = 0; i < sparkCount; i++) {
      this.sparkPool.push({
        x: 0,
        y: 0,
        z: 0,
        vx: 0,
        vy: 0,
        vz: 0,
        life: 0,
        max: 1,
        on: false,
      });
    }
    this.sparkGeo = new THREE.BufferGeometry();
    this.sparkGeo.setAttribute('position', new THREE.BufferAttribute(kPos, 3));
    this.sparkGeo.setAttribute('color', new THREE.BufferAttribute(kCol, 3));
    this.sparkMat = new THREE.PointsMaterial({
      size: 0.15,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexColors: true,
      sizeAttenuation: true,
    });
    this.sparkPts = new THREE.Points(this.sparkGeo, this.sparkMat);
    this.root.add(this.sparkPts);

    const maxQuads = 500;
    const tPos = new Float32Array(maxQuads * 18);
    const tCol = new Float32Array(maxQuads * 18);
    this.tireMaxFloats = tPos.length;
    this.tireGeo = new THREE.BufferGeometry();
    this.tireGeo.setAttribute('position', new THREE.BufferAttribute(tPos, 3));
    this.tireGeo.setAttribute('color', new THREE.BufferAttribute(tCol, 3));
    this.tireMesh = new THREE.Mesh(
      this.tireGeo,
      new THREE.MeshBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    this.tireMesh.frustumCulled = false;
    this.root.add(this.tireMesh);

    parent.add(this.root);
  }

  dispose(): void {
    this.smokeGeo.dispose();
    this.smokeMat.dispose();
    this.nitroGeo.dispose();
    this.nitroMat.dispose();
    this.exhaustFlameGeo.dispose();
    this.exhaustFlameMat.dispose();
    this.sparkGeo.dispose();
    this.sparkMat.dispose();
    this.tireGeo.dispose();
    (this.tireMesh.material as THREE.MeshBasicMaterial).dispose();
    this.root.removeFromParent();
  }

  update(dt: number, s: DrivingVfxInput): void {
    this.updateSmoke(dt, s);
    this.updateNitro(dt, s);
    this.updateExhaustFlames(dt, s);
    this.updateSparks(dt, s);
    this.updateTires(dt, s);
  }

  private spawnDriftSmoke(
    p: SmokeP,
    pos: THREE.Vector3,
    heading: number,
    speed: number,
    intensity: number = 1,
  ): void {
    const spread = 0.8 + intensity * 0.6;
    p.x = pos.x + (Math.random() - 0.5) * spread;
    p.y = pos.y + Math.random() * 0.15;
    p.z = pos.z + (Math.random() - 0.5) * spread;
    const f = Math.sin(heading);
    const g = Math.cos(heading);
    const backSpeed = Math.min(speed * 0.35, 10) * (0.8 + intensity * 0.4);
    const sideSpread = 4 + intensity * 3;
    p.vx = -f * backSpeed + (Math.random() - 0.5) * sideSpread;
    p.vz = -g * backSpeed + (Math.random() - 0.5) * sideSpread;
    p.vy = 1.0 + Math.random() * 2.0 + intensity * 1.5;
    p.max = 2.2 + Math.random() * 1.5 + intensity * 0.8;
    p.life = p.max;
    p.on = true;
  }

  private updateSmoke(dt: number, s: DrivingVfxInput): void {
    const pool = this.smokePool;
    const pos = this.smokeGeo.attributes.position.array as Float32Array;
    const col = this.smokeGeo.attributes.color.array as Float32Array;

    for (const p of pool) {
      if (!p.on) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.on = false;
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.vy += 0.5 * dt;
      p.vx *= 0.97;
      p.vz *= 0.97;
    }

    // DRIFT SMOKE
    if (s.handbrake && s.speed > 8) {
      const driftIntensity = s.driftAmount ?? Math.min(1, s.speed / 40);
      const baseRate = 25 + s.speed * 0.6; // Reduced spawn rate
      const cinematicBoost = driftIntensity > 0.5 ? 1.5 : 1.0;
      const spawnRate = baseRate * cinematicBoost;
      this.smokeAcc += dt * spawnRate;
      const L = rearWheel(s.x, s.z, s.heading, -1);
      const R = rearWheel(s.x, s.z, s.heading, 1);
      while (this.smokeAcc >= 1) {
        this.smokeAcc -= 1;
        const slot = pool.find((q) => !q.on);
        if (slot) {
          this.spawnDriftSmoke(
            slot,
            Math.random() < 0.5 ? L : R,
            s.heading,
            s.speed,
            driftIntensity,
          );
        }
      }
    } else {
      this.smokeAcc = Math.max(0, this.smokeAcc - dt * 20);
    }
    
    // BURNOUT SMOKE - when revving hard at low speed (tire smoke!)
    const isRevving = s.revving || (s.throttle > 0.8 && s.speed < 10);
    if (isRevving) {
      const burnoutIntensity = Math.min(1, s.throttle * 1.5);
      this.burnoutSmokeAcc += dt * 20 * burnoutIntensity;
      const L = rearWheel(s.x, s.z, s.heading, -1);
      const R = rearWheel(s.x, s.z, s.heading, 1);
      while (this.burnoutSmokeAcc >= 1) {
        this.burnoutSmokeAcc -= 1;
        const slot = pool.find((q) => !q.on);
        if (slot) {
          this.spawnDriftSmoke(
            slot,
            Math.random() < 0.5 ? L : R,
            s.heading,
            s.speed + 15, // Fake higher speed for more dramatic smoke
            burnoutIntensity * 1.5,
          );
        }
      }
    } else {
      this.burnoutSmokeAcc = Math.max(0, this.burnoutSmokeAcc - dt * 40);
    }

    for (let i = 0; i < pool.length; i++) {
      const p = pool[i];
      const j = i * 3;
      if (!p.on) {
        pos[j] = 0;
        pos[j + 1] = -5000;
        pos[j + 2] = 0;
        col[j] = 0.7;
        col[j + 1] = 0.7;
        col[j + 2] = 0.72;
        continue;
      }
      pos[j] = p.x;
      pos[j + 1] = p.y;
      pos[j + 2] = p.z;
      const t = p.life / p.max;
      const fadeIn = Math.min(1, (p.max - p.life) * 4);
      const fadeOut = t * t;
      const alpha = fadeIn * fadeOut * 0.85;
      const warmth = (1 - t) * 0.08;
      const grey = 0.78 + (1 - t) * 0.18;
      col[j] = (grey + warmth * 0.5) * alpha;
      col[j + 1] = grey * alpha;
      col[j + 2] = (grey - warmth * 0.3) * alpha;
    }
    this.smokeGeo.attributes.position.needsUpdate = true;
    this.smokeGeo.attributes.color.needsUpdate = true;
  }

  private updateNitro(dt: number, s: DrivingVfxInput): void {
    const pool = this.nitroPool;
    const pos = this.nitroGeo.attributes.position.array as Float32Array;
    const col = this.nitroGeo.attributes.color.array as Float32Array;

    for (const p of pool) {
      if (!p.on) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.on = false;
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.vy -= 2 * dt;
    }

    if (s.nitroBoosting && s.speed > 1.2) {
      this.nitroAcc += dt * 90; // Reduced from 180
      const eL = exhaustWorld(s.x, s.z, s.heading, -1);
      const eR = exhaustWorld(s.x, s.z, s.heading, 1);
      while (this.nitroAcc >= 1) {
        this.nitroAcc -= 1;
        const slot = pool.find((q) => !q.on);
        if (!slot) break;
        const e = Math.random() < 0.5 ? eL : eR;
        slot.x = e.x + (Math.random() - 0.5) * 0.2;
        slot.y = e.y + (Math.random() - 0.5) * 0.1;
        slot.z = e.z + (Math.random() - 0.5) * 0.2;
        const f = Math.sin(s.heading);
        const g = Math.cos(s.heading);
        const speed = 12 + Math.random() * 8;
        slot.vx = -f * speed + (Math.random() - 0.5) * 2;
        slot.vz = -g * speed + (Math.random() - 0.5) * 2;
        slot.vy = (Math.random() - 0.2) * 3;
        slot.max = 0.25 + Math.random() * 0.2;
        slot.life = slot.max;
        slot.on = true;
      }
    } else {
      this.nitroAcc = 0;
    }

    for (let i = 0; i < pool.length; i++) {
      const p = pool[i];
      const j = i * 3;
      if (!p.on) {
        pos[j] = 0;
        pos[j + 1] = -5000;
        pos[j + 2] = 0;
        col[j] = 1;
        col[j + 1] = 0.5;
        col[j + 2] = 0;
        continue;
      }
      pos[j] = p.x;
      pos[j + 1] = p.y;
      pos[j + 2] = p.z;
      const t = p.life / p.max;
      col[j] = 1.0;
      col[j + 1] = 0.3 + t * 0.5;
      col[j + 2] = t * 0.3;
    }
    this.nitroGeo.attributes.position.needsUpdate = true;
    this.nitroGeo.attributes.color.needsUpdate = true;
  }

  /** EXHAUST FLAMES - fire shoots from exhaust when throttle is high or revving */
  private updateExhaustFlames(dt: number, s: DrivingVfxInput): void {
    const pool = this.exhaustFlamePool;
    const pos = this.exhaustFlameGeo.attributes.position.array as Float32Array;
    const col = this.exhaustFlameGeo.attributes.color.array as Float32Array;

    // Update existing particles
    for (const p of pool) {
      if (!p.on) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.on = false;
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.vy -= 1.5 * dt;
    }

    // Spawn flames when throttle is high OR revving hard
    const rpmRatio = s.rpmRatio ?? (s.speed / 70);
    const isRevving = s.revving || (s.throttle > 0.7 && s.speed < 15);
    const highThrottle = s.throttle > 0.6 && rpmRatio > 0.5;
    const shouldFlame = isRevving || highThrottle || s.nitroBoosting;
    
    if (shouldFlame) {
      // More flames when revving hard
      const intensity = isRevving ? 2.0 : (s.nitroBoosting ? 1.5 : 1.0);
      const spawnRate = 30 * intensity * (0.5 + s.throttle * 0.5); // Reduced from 60
      this.exhaustFlameAcc += dt * spawnRate;
      
      const eL = exhaustWorld(s.x, s.z, s.heading, -1);
      const eR = exhaustWorld(s.x, s.z, s.heading, 1);
      
      while (this.exhaustFlameAcc >= 1) {
        this.exhaustFlameAcc -= 1;
        const slot = pool.find((q) => !q.on);
        if (!slot) break;
        
        const e = Math.random() < 0.5 ? eL : eR;
        slot.x = e.x + (Math.random() - 0.5) * 0.15;
        slot.y = e.y + (Math.random() - 0.5) * 0.08;
        slot.z = e.z + (Math.random() - 0.5) * 0.15;
        
        const f = Math.sin(s.heading);
        const g = Math.cos(s.heading);
        const flameSpeed = 6 + Math.random() * 6 + (isRevving ? 4 : 0);
        slot.vx = -f * flameSpeed + (Math.random() - 0.5) * 1.5;
        slot.vz = -g * flameSpeed + (Math.random() - 0.5) * 1.5;
        slot.vy = (Math.random() - 0.3) * 2;
        slot.max = 0.15 + Math.random() * 0.15;
        slot.life = slot.max;
        slot.on = true;
      }
    } else {
      this.exhaustFlameAcc = Math.max(0, this.exhaustFlameAcc - dt * 30);
    }

    // Update GPU buffers
    for (let i = 0; i < pool.length; i++) {
      const p = pool[i];
      const j = i * 3;
      if (!p.on) {
        pos[j] = 0;
        pos[j + 1] = -5000;
        pos[j + 2] = 0;
        col[j] = 1;
        col[j + 1] = 0.5;
        col[j + 2] = 0;
        continue;
      }
      pos[j] = p.x;
      pos[j + 1] = p.y;
      pos[j + 2] = p.z;
      
      // Fire color gradient: white -> yellow -> orange -> red
      const t = p.life / p.max;
      const heat = t * t;
      col[j] = 1.0; // R always high
      col[j + 1] = 0.2 + heat * 0.7; // G fades
      col[j + 2] = heat * 0.4; // B only when hot
    }
    
    this.exhaustFlameGeo.attributes.position.needsUpdate = true;
    this.exhaustFlameGeo.attributes.color.needsUpdate = true;
  }

  private updateSparks(dt: number, s: DrivingVfxInput): void {
    const pool = this.sparkPool;
    const pos = this.sparkGeo.attributes.position.array as Float32Array;
    const col = this.sparkGeo.attributes.color.array as Float32Array;

    for (const p of pool) {
      if (!p.on) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.on = false;
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.vy -= 25 * dt;
      p.vx *= 0.98;
      p.vz *= 0.98;
    }

    const shouldSpawnSparks = s.collisionImpulse > 0.5 || 
      (s.wallScraping && s.speed > 20);

    if (shouldSpawnSparks) {
      const n = s.wallScraping 
        ? Math.min(pool.length, 3 + Math.floor(s.speed * 0.15))
        : Math.min(pool.length, 6 + Math.floor(s.collisionImpulse * 4));
      
      let spawned = 0;
      for (const p of pool) {
        if (p.on) continue;
        
        const sideOffset = (Math.random() < 0.5 ? -1 : 1) * CAR.WIDTH * 0.5;
        const sin = Math.sin(s.heading);
        const cos = Math.cos(s.heading);
        
        p.x = s.x + cos * sideOffset + (Math.random() - 0.5) * 0.3;
        p.y = CAR.HEIGHT * 0.25 + Math.random() * 0.2;
        p.z = s.z - sin * sideOffset + (Math.random() - 0.5) * 0.3;
        
        if (s.wallScraping) {
          p.vx = -cos * sideOffset * 10 + (Math.random() - 0.5) * 8;
          p.vz = sin * sideOffset * 10 + (Math.random() - 0.5) * 8;
          p.vy = 3 + Math.random() * 5;
        } else {
          p.vx = (Math.random() - 0.5) * 15;
          p.vz = (Math.random() - 0.5) * 15;
          p.vy = 6 + Math.random() * 10;
        }
        
        p.max = 0.15 + Math.random() * 0.2;
        p.life = p.max;
        p.on = true;
        spawned++;
        if (spawned >= n) break;
      }
    }

    for (let i = 0; i < pool.length; i++) {
      const p = pool[i];
      const j = i * 3;
      if (!p.on) {
        pos[j] = 0;
        pos[j + 1] = -5000;
        pos[j + 2] = 0;
        col[j] = 1;
        col[j + 1] = 0.8;
        col[j + 2] = 0.3;
        continue;
      }
      pos[j] = p.x;
      pos[j + 1] = p.y;
      pos[j + 2] = p.z;
      const t = p.life / p.max;
      col[j] = 1.0;
      col[j + 1] = 0.6 + t * 0.4;
      col[j + 2] = t * 0.5;
    }
    this.sparkGeo.attributes.position.needsUpdate = true;
    this.sparkGeo.attributes.color.needsUpdate = true;
  }

  private updateTires(_dt: number, s: DrivingVfxInput): void {
    const drift = s.handbrake && s.speed > 10;
    const wheelL = rearWheel(s.x, s.z, s.heading, -1);
    const wheelR = rearWheel(s.x, s.z, s.heading, 1);

    if (drift) {
      if (!this.hadTireL) {
        this.lastTireLX = wheelL.x;
        this.lastTireLZ = wheelL.z;
        this.hadTireL = true;
      } else {
        const d = Math.hypot(wheelL.x - this.lastTireLX, wheelL.z - this.lastTireLZ);
        if (d >= 0.4) {
          this.pushTireSegment(this.lastTireLX, this.lastTireLZ, wheelL.x, wheelL.z, s.speed);
          this.lastTireLX = wheelL.x;
          this.lastTireLZ = wheelL.z;
        }
      }
      if (!this.hadTireR) {
        this.lastTireRX = wheelR.x;
        this.lastTireRZ = wheelR.z;
        this.hadTireR = true;
      } else {
        const d = Math.hypot(wheelR.x - this.lastTireRX, wheelR.z - this.lastTireRZ);
        if (d >= 0.4) {
          this.pushTireSegment(this.lastTireRX, this.lastTireRZ, wheelR.x, wheelR.z, s.speed);
          this.lastTireRX = wheelR.x;
          this.lastTireRZ = wheelR.z;
        }
      }
    } else {
      this.hadTireL = false;
      this.hadTireR = false;
    }

    this.tireGeo.setDrawRange(0, this.tireVertexCount / 3);
    this.tireGeo.attributes.position.needsUpdate = true;
    this.tireGeo.attributes.color.needsUpdate = true;
  }

  private pushTireSegment(
    x0: number,
    z0: number,
    x1: number,
    z1: number,
    speed: number,
  ): void {
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len = Math.hypot(dx, dz) || 1e-6;
    const tireWidth = 0.25;
    const px = (-dz / len) * tireWidth * 0.5;
    const pz = (dx / len) * tireWidth * 0.5;
    const y = 0.03;
    
    const posArr = this.tireGeo.attributes.position.array as Float32Array;
    const colArr = this.tireGeo.attributes.color.array as Float32Array;
    
    if (this.tireVertexCount + 18 > this.tireMaxFloats) {
      this.tireVertexCount = 0;
    }
    
    const darkness = Math.min(0.12, 0.04 + speed * 0.001);
    
    let v = this.tireVertexCount;
    const pushVert = (vx: number, vy: number, vz: number) => {
      posArr[v] = vx;
      posArr[v + 1] = vy;
      posArr[v + 2] = vz;
      colArr[v] = darkness;
      colArr[v + 1] = darkness;
      colArr[v + 2] = darkness;
      v += 3;
    };
    
    pushVert(x0 + px, y, z0 + pz);
    pushVert(x0 - px, y, z0 - pz);
    pushVert(x1 + px, y, z1 + pz);
    pushVert(x0 - px, y, z0 - pz);
    pushVert(x1 - px, y, z1 - pz);
    pushVert(x1 + px, y, z1 + pz);
    
    this.tireVertexCount = v;
  }
}
